/**
 * Enterprise Analytics Calculations
 * 
 * Provides calculations for profit analysis, customer analytics,
 * forecasting, and ABC analysis
 */

export interface ProfitMetrics {
  grossProfit: number;
  grossMargin: number;
  netProfit: number;
  netMargin: number;
  costOfGoodsSold: number;
  operatingExpenses: number;
}

export interface CustomerSegment {
  segment: 'new' | 'returning' | 'vip';
  count: number;
  revenue: number;
  avgOrderValue: number;
}

export interface CustomerLifetimeValue {
  customerId: string;
  customerName: string;
  totalRevenue: number;
  orderCount: number;
  avgOrderValue: number;
  firstOrderDate: Date;
  lastOrderDate: Date;
  lifetimeValue: number;
  purchaseFrequency: number;
  customerLifespan: number; // in days
}

export interface ForecastData {
  date: string;
  value: number;
  confidenceLower?: number;
  confidenceUpper?: number;
}

export interface ABCClassification {
  category: 'A' | 'B' | 'C';
  products: Array<{
    id: string;
    name: string;
    value: number;
    percentage: number;
    cumulativePercentage: number;
  }>;
}

/**
 * Calculate gross profit and margins
 */
export function calculateProfitMetrics(
  revenue: number,
  costOfGoodsSold: number,
  operatingExpenses: number = 0
): ProfitMetrics {
  const grossProfit = revenue - costOfGoodsSold;
  const grossMargin = revenue > 0 ? (grossProfit / revenue) * 100 : 0;
  const netProfit = grossProfit - operatingExpenses;
  const netMargin = revenue > 0 ? (netProfit / revenue) * 100 : 0;

  return {
    grossProfit,
    grossMargin,
    netProfit,
    netMargin,
    costOfGoodsSold,
    operatingExpenses,
  };
}

/**
 * Calculate customer lifetime value
 * CLV = Average Order Value x Purchase Frequency (per month) x Projected Lifespan (12 months)
 */
export function calculateCustomerLifetimeValue(
  totalRevenue: number,
  orderCount: number,
  customerLifespanDays: number
): number {
  if (orderCount === 0 || customerLifespanDays === 0) return 0;

  const avgOrderValue = totalRevenue / orderCount;
  const monthsActive = Math.max(customerLifespanDays / 30, 1);
  const purchaseFrequencyPerMonth = orderCount / monthsActive;
  const projectedLifespanMonths = 12;

  return avgOrderValue * purchaseFrequencyPerMonth * projectedLifespanMonths;
}

/**
 * Segment customers into new, returning, and VIP
 */
export function segmentCustomers(
  customers: Array<{
    id: string;
    orderCount: number;
    totalRevenue: number;
    firstOrderDate: Date;
    lastOrderDate: Date;
  }>,
  vipThreshold: number = 1000
): CustomerSegment[] {
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  const vipCustomers = customers.filter(
    c => c.totalRevenue >= vipThreshold
  );
  const vipIds = new Set(vipCustomers.map(c => c.id));
  const newCustomers = customers.filter(
    c => c.firstOrderDate >= thirtyDaysAgo && !vipIds.has(c.id)
  );
  const returningCustomers = customers.filter(
    c => c.firstOrderDate < thirtyDaysAgo && c.orderCount > 1 && !vipIds.has(c.id)
  );

  const calculateSegmentStats = (segment: typeof customers) => {
    const count = segment.length;
    const revenue = segment.reduce((sum, c) => sum + c.totalRevenue, 0);
    const avgOrderValue = count > 0 ? revenue / segment.reduce((sum, c) => sum + c.orderCount, 0) : 0;
    return { count, revenue, avgOrderValue };
  };

  return [
    {
      segment: 'new',
      ...calculateSegmentStats(newCustomers),
    },
    {
      segment: 'returning',
      ...calculateSegmentStats(returningCustomers),
    },
    {
      segment: 'vip',
      ...calculateSegmentStats(vipCustomers),
    },
  ];
}

/**
 * Simple linear regression for forecasting
 */
export function linearRegressionForecast(
  historicalData: Array<{ date: Date; value: number }>,
  futurePeriods: number
): ForecastData[] {
  if (historicalData.length < 3) {
    return [];
  }

  const sorted = [...historicalData].sort((a, b) => a.date.getTime() - b.date.getTime());

  const dayMap = new Map<string, { sum: number; count: number }>();
  sorted.forEach(d => {
    const key = d.date.toISOString().split('T')[0];
    const existing = dayMap.get(key) || { sum: 0, count: 0 };
    dayMap.set(key, { sum: existing.sum + d.value, count: existing.count + 1 });
  });

  const baseDate = sorted[0].date.getTime();
  const msPerDay = 86400000;
  const aggregated = Array.from(dayMap.entries())
    .map(([dateStr, { sum }]) => ({
      x: (new Date(dateStr).getTime() - baseDate) / msPerDay,
      y: sum,
      dateStr,
    }))
    .sort((a, b) => a.x - b.x);

  const n = aggregated.length;
  if (n < 3) return [];

  const xValues = aggregated.map(d => d.x);
  const yValues = aggregated.map(d => d.y);

  const sumX = xValues.reduce((s, x) => s + x, 0);
  const sumY = yValues.reduce((s, y) => s + y, 0);
  const sumXY = xValues.reduce((s, x, i) => s + x * yValues[i], 0);
  const sumXX = xValues.reduce((s, x) => s + x * x, 0);

  const denom = n * sumXX - sumX * sumX;
  if (denom === 0) return [];

  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;

  const residuals = yValues.map((y, i) => y - (slope * xValues[i] + intercept));
  const sumSquaredResiduals = residuals.reduce((s, r) => s + r * r, 0);
  const standardError = Math.sqrt(sumSquaredResiduals / (n - 2));
  const meanX = sumX / n;
  const sxx = sumXX - (sumX * sumX) / n;

  const lastDayX = xValues[xValues.length - 1];
  const lastDate = new Date(sorted[sorted.length - 1].date);
  const forecast: ForecastData[] = [];

  for (let i = 1; i <= futurePeriods; i++) {
    const futureX = lastDayX + i;
    const predictedValue = slope * futureX + intercept;
    const distFromMean = futureX - meanX;
    const predictionSE = standardError * Math.sqrt(1 + 1 / n + (distFromMean * distFromMean) / sxx);
    const ci = 1.96 * predictionSE;

    const forecastDate = new Date(lastDate);
    forecastDate.setDate(forecastDate.getDate() + i);

    forecast.push({
      date: forecastDate.toISOString().split('T')[0],
      value: Math.max(0, predictedValue),
      confidenceLower: Math.max(0, predictedValue - ci),
      confidenceUpper: predictedValue + ci,
    });
  }

  return forecast;
}

/**
 * ABC Analysis - Classify products by value contribution
 * A: 80% of value, B: 15% of value, C: 5% of value
 */
export function performABCAnalysis(
  products: Array<{ id: string; name: string; value: number }>
): ABCClassification {
  // Sort by value descending
  const sorted = [...products].sort((a, b) => b.value - a.value);
  
  const totalValue = sorted.reduce((sum, p) => sum + p.value, 0);
  if (totalValue === 0) {
    return {
      category: 'A',
      products: [],
    };
  }
  
  let cumulativeValue = 0;
  const classified: ABCClassification[] = [
    { category: 'A', products: [] },
    { category: 'B', products: [] },
    { category: 'C', products: [] },
  ];
  
  sorted.forEach((product, index) => {
    cumulativeValue += product.value;
    const cumulativePercentage = (cumulativeValue / totalValue) * 100;
    const percentage = (product.value / totalValue) * 100;
    
    let category: 'A' | 'B' | 'C';
    if (cumulativePercentage <= 80) {
      category = 'A';
    } else if (cumulativePercentage <= 95) {
      category = 'B';
    } else {
      category = 'C';
    }
    
    classified.find(c => c.category === category)!.products.push({
      ...product,
      percentage,
      cumulativePercentage,
    });
  });
  
  // Return all classifications (for display purposes, we'll use the first one as default)
  return classified[0];
}

/**
 * Get all ABC classifications
 */
export function getAllABCClassifications(
  products: Array<{ id: string; name: string; value: number }>
): { A: ABCClassification; B: ABCClassification; C: ABCClassification } {
  const sorted = [...products].sort((a, b) => b.value - a.value);
  const totalValue = sorted.reduce((sum, p) => sum + p.value, 0);
  
  let cumulativeValue = 0;
  const result = {
    A: { category: 'A' as const, products: [] as ABCClassification['products'] },
    B: { category: 'B' as const, products: [] as ABCClassification['products'] },
    C: { category: 'C' as const, products: [] as ABCClassification['products'] },
  };
  
  sorted.forEach((product) => {
    cumulativeValue += product.value;
    const cumulativePercentage = (cumulativeValue / totalValue) * 100;
    const percentage = (product.value / totalValue) * 100;
    
    let category: 'A' | 'B' | 'C';
    if (cumulativePercentage <= 80) {
      category = 'A';
    } else if (cumulativePercentage <= 95) {
      category = 'B';
    } else {
      category = 'C';
    }
    
    result[category].products.push({
      ...product,
      percentage,
      cumulativePercentage,
    });
  });
  
  return result;
}

/**
 * Calculate ROI (Return on Investment)
 * ROI = (Net Profit / Investment) * 100
 */
export function calculateROI(netProfit: number, investment: number): number {
  if (investment === 0) return 0;
  return (netProfit / investment) * 100;
}

/**
 * Calculate break-even point
 * Break-even = Fixed Costs / (Price - Variable Cost per Unit)
 */
export function calculateBreakEven(
  fixedCosts: number,
  pricePerUnit: number,
  variableCostPerUnit: number
): number {
  const contributionMargin = pricePerUnit - variableCostPerUnit;
  if (contributionMargin <= 0) return Infinity; // Cannot break even
  return fixedCosts / contributionMargin;
}
